import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';

import { FormikRadioGroup } from '@/components/formik/FormikRadioGroup';
import { ChevronLeft } from '@/components/icons/ChevronLeft';
import { IconTooltipButton } from '@/components/iconTooltipButton/IconTooltipButton';
import { Loader } from '@/components/Loader';

import type { TAddPaymentMethodForm, TPaymentMethodListItem } from '@/types/wallet';

import { paymentMethodsQuery } from '@/queries/wallet';
import { paymentMethodSchema } from '@/validation/wallet';

import { AddNewMethod } from './AddNewMethod';
import { mainStyles as styles } from './PaymentMethod.styles';
import { SelectExistingMethod } from './SelectExistingMethod';

type TState = 'main' | 'selectExisting' | 'addNew' | 'addFirst';

type TPaymentMethodsProps = {
  titles: Record<TState, string>;
  disabled?: Set<number>;
};

const ACTIONS = [
  { value: 'selectExisting', label: 'stationSetup.connectBank.options.selectExisting' },
  { value: 'addNew', label: 'stationSetup.connectBank.options.addNew' },
];

// Stable empty list while the payment methods have not loaded (a new array each render would
// re-run effects that depend on it).
const NO_METHODS: TPaymentMethodListItem[] = [];

/**
 * Payment method step of the Add Payment Method modal and the create-station bank step: choose
 * an existing bank account or add a new one. Lives inside a formik form (`values.action`).
 *
 * Data: the payment methods are read straight from the saved query data (`paymentMethodsQuery`);
 * the component keeps no copy. A save (W9 to W11) marks the list old and it refreshes by itself.
 * Spinner: only while there is no answer yet (`isLoading`), never during a background refresh.
 * Initial `action`: set ONCE, when the first answer arrives: no methods (an empty list, or an
 * empty answer saved as `null`) -> `addNew`; the form
 * started with a `paymentMethodId` -> `selectExisting`. Later refreshes (focus, after a save) never
 * write form values, so they cannot move the operator off the screen they chose.
 * Constraint: a failed load shows no error here (as before); the list stays empty.
 */
export const PaymentMethod = ({ titles, disabled }: TPaymentMethodsProps) => {
  const { t } = useTranslation();
  const { data, isLoading, isSuccess } = useQuery(paymentMethodsQuery());
  const { values, setFieldValue, isSubmitting, setFormikState } =
    useFormikContext<TAddPaymentMethodForm>();

  const methods = data ?? NO_METHODS;

  const initialData = useRef(values.data);
  const actionInitialised = useRef(false);
  const isFirst = methods.length === 0;

  useEffect(() => {
    // ToDo: Handle the error
    // `isSuccess`, not `data`: an empty answer (200 `{}`) saves `null`, which means "no methods"
    // and must start the form on "add new", the same as an empty list. A failed load is not
    // success, so it still picks nothing.
    if (!isSuccess || actionInitialised.current) {
      return;
    }

    actionInitialised.current = true;

    if (!data || data.length === 0) {
      setFieldValue('action', 'addNew');
    } else if ('paymentMethodId' in initialData.current) {
      setFieldValue('action', 'selectExisting');
    }
  }, [data, isSuccess, setFieldValue]);

  useEffect(() => {
    const stateValues = paymentMethodSchema.cast({
      action: values.action,
      data: initialData.current,
    }) as TAddPaymentMethodForm;

    setFormikState((prev) => ({
      ...prev,
      values: stateValues,
      submitCount: 0,
    }));
  }, [values.action, setFormikState]);

  const getContent = () => {
    switch (values.action) {
      case 'addNew':
        return <AddNewMethod />;

      case 'selectExisting':
        return <SelectExistingMethod methods={methods} disabled={disabled} />;

      default:
        return (
          <FormikRadioGroup
            variant="button"
            name="action"
            options={ACTIONS}
            sx={styles.options}
            renderOption={({ label }) => t(label)}
          />
        );
    }
  };

  const getTitle = () => {
    switch (values.action) {
      case 'addNew':
        return isFirst ? titles.addFirst : titles.addNew;

      case 'selectExisting':
        return titles.selectExisting;

      default:
        return titles.main;
    }
  };

  return (
    <>
      <Loader active={isLoading} position="absolute" />

      <Box>
        <Box sx={styles.header}>
          <Typography variant="h6">{t(getTitle())}</Typography>

          {values.action && !isFirst && (
            <IconTooltipButton
              size="small"
              label={t('common.back')}
              onClick={() => setFieldValue('action', null)}
              disabled={isSubmitting}
              sx={styles.backButton}
            >
              <ChevronLeft />
            </IconTooltipButton>
          )}
        </Box>

        {getContent()}
      </Box>
    </>
  );
};
